import { useEffect, useRef, useState } from "react";
import { useMe } from "@/lib/auth";
import { useDocumentMeta } from "@/lib/useDocumentMeta";


import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useTranslation } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { auth, db, storage } from "@/lib/firebase";
import { signInWithCustomToken, signOut } from "firebase/auth";
import {
  collection,
  query,
  orderBy,
  onSnapshot,
  addDoc,
  doc,
  setDoc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
  getDocs,
  limit,
} from "firebase/firestore";
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import { Send, AlertCircle, Loader2, Paperclip, FileText, Image, Pencil, Trash2 } from "lucide-react";

async function convertHeicToJpegIfNeeded(file: File): Promise<File | Blob> {
  const isHeic =
    file.name.toLowerCase().endsWith(".heic") ||
    file.name.toLowerCase().endsWith(".heif") ||
    file.type === "image/heic" ||
    file.type === "image/heif";

  if (!isHeic) return file;

  try {
    const heic2anyModule = await import("heic2any");
    const heic2any = heic2anyModule.default;

    const result = await heic2any({
      blob: file,
      toType: "image/jpeg",
      quality: 0.8,
    });

    const convertedBlob = Array.isArray(result) ? result[0] : result;
    const newName = file.name.substring(0, file.name.lastIndexOf(".")) + ".jpg";
    return new File([convertedBlob], newName, { type: "image/jpeg" });
  } catch (err) {
    console.error("[HEIC Conversion] failed, using original file:", err);
    return file;
  }
}

type ChatMessage = {
  id: string;
  sender: "Customer" | "Admin";
  content: string;
  isRead: boolean;
  createdAt: string;
  fileUrl?: string;
  fileName?: string;
  fileType?: string;
  isEdited?: boolean;
};

function getFormattedDateDivider(dateStr: string): string {
  const date = new Date(dateStr);
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);

  if (date.toDateString() === today.toDateString()) {
    return "Today";
  } else if (date.toDateString() === yesterday.toDateString()) {
    return "Yesterday";
  } else {
    return date.toLocaleDateString("en-US", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  }
}

function renderMessageTextWithLinks(text: string) {
  if (!text) return "";
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  const parts = text.split(urlRegex);
  return parts.map((part, index) => {
    if (part.startsWith("http://") || part.startsWith("https://")) {
      return (
        <a
          key={index}
          href={part}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-600 hover:text-blue-800 underline break-all font-semibold"
        >
          {part}
        </a>
      );
    }
    return part;
  });
}

import { getTenantConfig } from "@/lib/tenant";

export function Consultations() {
  const tenant = getTenantConfig();
  const { t } = useTranslation();
  useDocumentMeta({
    title: `${t("consultations_title")} · ${tenant.brandName}`,
    description: t("consultations_subtitle"),
  });


  const { data: me, isLoading: meLoading } = useMe();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputText, setInputText] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const [firebaseError, setFirebaseError] = useState<string | null>(null);
  
  const [editingMessageId, setEditingMessageId] = useState<string | null>(null);
  const [editingText, setEditingText] = useState("");
  
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);
  const textInputRef = useRef<HTMLTextAreaElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const isNearBottomRef = useRef(true);

  useEffect(() => {
    const el = textInputRef.current;
    if (el) {
      el.style.height = "auto";
      const newHeight = Math.min(Math.max(el.scrollHeight, 44), 160);
      el.style.height = `${newHeight}px`;
    }
  }, [inputText]);

  async function syncLastMessage(chatId: string) {
    try {
      const q = query(
        collection(db, "chats", chatId, "messages"),
        orderBy("createdAt", "desc"),
        limit(1)
      );
      const snap = await getDocs(q);
      if (snap.empty) {
        await updateDoc(doc(db, "chats", chatId), {
          lastMessageText: "",
          lastMessageSender: "",
        });
      } else {
        const latest = snap.docs[0].data();
        let text = latest.content || "";
        if (latest.fileUrl) {
          text = `Attachment: ${latest.fileName || "file"}`;
        }
        await updateDoc(doc(db, "chats", chatId), {
          lastMessageText: text,
          lastMessageSender: latest.sender || "",
        });
      }
    } catch (e) {
      console.error("Failed to sync last message:", e);
    }
  }

  async function handleDeleteMessage(messageId: string) {
    if (!auth.currentUser) return;
    if (!window.confirm("Are you sure you want to delete this message?")) return;
    const userId = auth.currentUser.uid;
    try {
      await deleteDoc(doc(db, "chats", userId, "messages", messageId));
      await syncLastMessage(userId);
    } catch (e) {
      console.error("[Firestore] failed deleting message:", e);
    }
  }

  async function handleUpdateMessage(messageId: string) {
    if (!auth.currentUser || !editingText.trim()) return;
    const userId = auth.currentUser.uid;
    try {
      await updateDoc(doc(db, "chats", userId, "messages", messageId), {
        content: editingText.trim(),
        isEdited: true,
      });
      setEditingMessageId(null);
      setEditingText("");
      await syncLastMessage(userId);
    } catch (e) {
      console.error("[Firestore] failed updating message:", e);
    }
  }
 
  // Eligibility check
  const isEligible = Boolean(me?.hasCoachSubscription);





  // Prevent browser window scroll/bounce on iOS when input focuses
  useEffect(() => {
    const preventScroll = () => {
      window.scrollTo(0, 0);
    };

    const html = document.documentElement;
    const body = document.body;
    
    html.style.position = "fixed";
    html.style.overflow = "hidden";
    html.style.width = "100%";
    html.style.height = "100%";
    
    body.style.position = "fixed";
    body.style.overflow = "hidden";
    body.style.width = "100%";
    body.style.height = "100%";

    window.addEventListener("scroll", preventScroll);

    return () => {
      html.style.position = "";
      html.style.overflow = "";
      html.style.width = "";
      html.style.height = "";
      
      body.style.position = "";
      body.style.overflow = "";
      body.style.width = "";
      body.style.height = "";

      window.removeEventListener("scroll", preventScroll);
    };
  }, []);

  // Connect and listen to Firestore in real-time
  useEffect(() => {
    if (meLoading) return;

    if (!isEligible || !me?.firebaseCustomToken) {
      setLoading(false);
      return;
    }
     let isCancelled = false;
    let unsubscribe = () => {};
    setLoading(true);
 
    const getUidFromToken = (token: string) => {
      try {
        const payload = token.split(".")[1];
        const base64 = payload.replace(/-/g, "+").replace(/_/g, "/");
        const decoded = JSON.parse(atob(base64));
        return decoded.uid;
      } catch (e) {
        console.error("[Firebase Custom Token] decode failed:", e);
        return null;
      }
    };

    const initChat = async () => {
      try {
        const expectedUid = getUidFromToken(me.firebaseCustomToken!);
        let userId = auth.currentUser?.uid;

        // Only authenticate if not already signed in with the correct UID
        if (!userId || userId !== expectedUid) {
          try {
            await signOut(auth);
          } catch (e) {
            console.warn("[Firebase Auth] signOut warning:", e);
          }
          const userCredential = await signInWithCustomToken(auth, me.firebaseCustomToken!);
          userId = userCredential.user.uid;
        }

        if (isCancelled) return;

        // Ensure chat metadata entry exists on first load
        const chatRef = doc(db, "chats", userId);
        await setDoc(
          chatRef,
          {
            userId,
            customerEmail: me.email,
          },
          { merge: true }
        );

        if (isCancelled) return;

        // Subscribe to subcollection messages in real-time
        const q = query(
          collection(db, "chats", userId, "messages"),
          orderBy("createdAt", "asc")
        );

        unsubscribe = onSnapshot(
          q,
          (snapshot) => {
            if (isCancelled) return;
            const list = snapshot.docs.map((doc) => {
              const data = doc.data();
              let dateStr = new Date().toISOString();
              try {
                if (data.createdAt && typeof data.createdAt.toDate === "function") {
                  const dateVal = data.createdAt.toDate();
                  if (dateVal) dateStr = dateVal.toISOString();
                }
              } catch (e) {
                console.warn("[Firestore] Failed parsing date, falling back to now", e);
              }
              return {
                id: doc.id,
                sender: data.sender,
                content: data.content,
                isRead: data.isRead || false,
                isEdited: data.isEdited || false,
                createdAt: dateStr,
                fileUrl: data.fileUrl,
                fileName: data.fileName,
                fileType: data.fileType,
              } as ChatMessage;
            });
            setMessages(list);

            const unreadAdminMsgs = snapshot.docs.filter((d) => {
              const data = d.data();
              return data.sender === "Admin" && !data.isRead;
            });
            if (unreadAdminMsgs.length > 0) {
              unreadAdminMsgs.forEach((d) => {
                updateDoc(doc(db, "chats", userId, "messages", d.id), {
                  isRead: true,
                }).catch((err) => console.error("Error marking message as read:", err));
              });
            }

            setLoading(false);
            setFirebaseError(null);
          },
          (err) => {
            if (isCancelled) return;
            console.error("[Firestore] stream error:", err);
            setFirebaseError("Failed to connect to chat history feed.");
            setLoading(false);
          }
        );
      } catch (err: any) {
        if (isCancelled) return;
        console.error("[Firebase Auth] error:", err);
        setFirebaseError("Authentication with chat network failed.");
        setLoading(false);
      }
    };

    initChat();

    return () => {
      isCancelled = true;
      unsubscribe();
    };
  }, [isEligible, me?.firebaseCustomToken, me?.email, me?.funnelSource, me?.hasCoachSubscription, meLoading]);
 
  const handleScroll = () => {
    const container = chatContainerRef.current;
    if (!container) return;
    const threshold = 150;
    const isNear = container.scrollHeight - container.scrollTop - container.clientHeight <= threshold;
    isNearBottomRef.current = isNear;
  };

  const scrollToBottom = (force = false) => {
    if (force || isNearBottomRef.current) {
      messagesEndRef.current?.scrollIntoView({ behavior: "auto" });
    }
  };

  useEffect(() => {
    scrollToBottom(true);
  }, [messages]);

  // Scroll to bottom when visual viewport height changes (e.g. keyboard open/close)
  useEffect(() => {
    if (!window.visualViewport) return;
    const handleResize = () => {
      scrollToBottom(false);
      setTimeout(() => scrollToBottom(false), 50);
      setTimeout(() => scrollToBottom(false), 150);
      setTimeout(() => scrollToBottom(false), 300);
      setTimeout(() => scrollToBottom(false), 400);
    };
    window.visualViewport.addEventListener("resize", handleResize);
    return () => {
      window.visualViewport?.removeEventListener("resize", handleResize);
    };
  }, []);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend(e);
    }
  };

  async function handleSend(e: React.FormEvent) {
    e.preventDefault();
    if (!inputText.trim() || !auth.currentUser || sending) return;

    const userId = auth.currentUser.uid;
    const bodyText = inputText.trim();
    setInputText("");
    textInputRef.current?.focus();
    setSending(true);

    try {
      // 1. Add message document
      await addDoc(collection(db, "chats", userId, "messages"), {
        sender: "Customer",
        content: bodyText,
        isRead: false,
        createdAt: serverTimestamp(),
      });

      // 2. Update chat metadata status
      const chatRef = doc(db, "chats", userId);
      await setDoc(
        chatRef,
        {
          status: messages.length === 0 ? "new" : "unread",
          updatedAt: serverTimestamp(),
          lastMessageText: bodyText,
          lastMessageSender: "Customer",
          customerEmail: me?.email,
        },
        { merge: true }
      );
    } catch (err: any) {
      console.error("[Firestore] send message failed:", err);
      setFirebaseError(`Failed to send message: ${err.message || err.code || err}`);
    } finally {
      setSending(false);
      textInputRef.current?.focus();
    }
  }

  // Helper to upload a file to Firebase Storage and save to Firestore
  async function uploadFile(originalFile: File) {
    if (!auth.currentUser) return;
    const userId = auth.currentUser.uid;
    setSending(true);
    setUploadProgress(0);

    let file = originalFile;
    try {
      file = (await convertHeicToJpegIfNeeded(originalFile)) as File;
    } catch (e) {
      console.warn("[HEIC Converter] failed, using original file:", e);
    }

    const fileRef = ref(storage, `chats/${userId}/uploads/${Date.now()}_${file.name}`);
    const uploadTask = uploadBytesResumable(fileRef, file);

    uploadTask.on(
      "state_changed",
      (snapshot) => {
        const progress = Math.round(
          (snapshot.bytesTransferred / snapshot.totalBytes) * 100
        );
        setUploadProgress(progress);
      },
      (error) => {
        console.error("[Firebase Storage] upload failed:", error);
        setFirebaseError("Failed to upload file. Please try again.");
        setSending(false);
        setUploadProgress(null);
      },
      async () => {
        try {
          const downloadUrl = await getDownloadURL(uploadTask.snapshot.ref);

          // 1. Add message with file attachment to subcollection
          await addDoc(collection(db, "chats", userId, "messages"), {
            sender: "Customer",
            content: `Sent an attachment: ${file.name}`,
            isRead: false,
            createdAt: serverTimestamp(),
            fileUrl: downloadUrl,
            fileName: file.name,
            fileType: file.type,
          });

          // 2. Update chat metadata status
          const chatRef = doc(db, "chats", userId);
          await setDoc(
            chatRef,
            {
              status: messages.length === 0 ? "new" : "unread",
              updatedAt: serverTimestamp(),
              lastMessageText: `Attachment: ${file.name}`,
              lastMessageSender: "Customer",
            },
            { merge: true }
          );
        } catch (err) {
          console.error("[Firestore] failed to save file message:", err);
        } finally {
          setSending(false);
          setUploadProgress(null);
          if (fileInputRef.current) fileInputRef.current.value = "";
          if (imageInputRef.current) imageInputRef.current.value = "";
        }
      }
    );
  }

  // Handle media (image/video) picker
  async function handleImageChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file || sending) return;
    const isHeic = file.name.toLowerCase().endsWith(".heic") || file.name.toLowerCase().endsWith(".heif");
    if (!file.type.startsWith("image/") && !file.type.startsWith("video/") && !isHeic) {
      setFirebaseError("Please select an image or video file only.");
      if (imageInputRef.current) imageInputRef.current.value = "";
      return;
    }
    await uploadFile(file);
  }

  // Handle document picker (excludes media)
  async function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file || sending) return;
    const isHeic = file.name.toLowerCase().endsWith(".heic") || file.name.toLowerCase().endsWith(".heif");
    if ((file.type.startsWith("image/") || file.type.startsWith("video/") || isHeic)) {
      setFirebaseError("Please use the Photo/Video button to upload media.");
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }
    await uploadFile(file);
  }

  // 1. Not eligible view
  if (!isEligible) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-16 lg:px-6">
        <Card className="border-destructive/30 bg-destructive/5">
          <CardHeader className="flex flex-row items-center gap-3">
            <AlertCircle className="size-6 text-destructive" />
            <CardTitle className="text-xl sm:text-2xl text-destructive">
              Access Restricted
            </CardTitle>
          </CardHeader>
          <CardContent className="text-base leading-relaxed text-muted-foreground">
            This 1-on-1 Consultation Chat is only available for users who purchased the Coaching Support package. Please upgrade your package or contact support if you believe this is an error.
          </CardContent>

        </Card>
      </div>
    );
  }

  // 2. Loading view
  if (loading || meLoading) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-32 flex flex-col items-center gap-4">
        <Loader2 className="size-8 animate-spin text-primary" />
        <p className="text-sm text-muted-foreground">Opening your consultation chat room…</p>
      </div>
    );
  }

  // 3. Error view
  if (firebaseError) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-16 lg:px-6">
        <Card className="border-destructive/30">
          <CardHeader>
            <CardTitle className="text-lg text-destructive">
              Chat connection failed
            </CardTitle>
          </CardHeader>
          <CardContent className="text-sm text-muted-foreground">
            {firebaseError}
            <div className="mt-4">
              <Button onClick={() => window.location.reload()} size="sm">
                Reload Room
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  let lastGroupDate = "";

  return (
    <div className="mx-auto max-w-3xl w-full px-0 py-0 sm:px-4 sm:py-6 lg:px-6 h-full flex-1 flex flex-col overflow-hidden sm:h-[82vh] sm:min-h-[600px]">
      <Card className="flex flex-col flex-1 shadow-sm hover:shadow-sm rounded-none sm:rounded-xl border-x-0 sm:border-x py-0 gap-0 h-full overflow-hidden">
        <div className="border-b border-border/40 py-3.5 px-4 sm:px-6">
          <div className="flex items-center gap-3">
            <img
              src="/emily-avatar.png"
              alt="Emily Walsh"
              className="size-10 rounded-full object-cover shadow-xs"
            />
            <div className="flex flex-col">
              <h2 className="text-sm sm:text-base font-bold text-foreground leading-tight">
                Emily Walsh
              </h2>
              <span className={`text-[11px] sm:text-xs font-medium leading-none mt-0.5 ${tenant.brandName === "MindfulMe" ? "text-indigo-600" : "text-[#FF6F61]"}`}>
                {tenant.brandName === "MindfulMe" ? "Relationship Coach" : "Family Coach"}
              </span>

            </div>
          </div>
        </div>

        {/* Messages list */}
        <div
          ref={chatContainerRef}
          onScroll={handleScroll}
          className="flex-1 overflow-y-auto p-4 bg-muted/10 flex flex-col"
        >
          {messages.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-center p-8 mt-12">
              <div className="bg-primary/5 rounded-full p-4 mb-3 text-primary">
                <AlertCircle className="size-6" />
              </div>
              <p className="text-sm font-semibold text-foreground">{t("chat_welcome")}</p>
              <p className="text-xs text-muted-foreground max-w-xs mt-1 leading-relaxed">
                {t("chat_welcome_desc")}
              </p>
            </div>
          ) : (
            <>
              <div className="flex-1 min-h-0" />
              {messages.map((msg, idx) => {
              const isAdmin = msg.sender === "Admin";
              const isImage = msg.fileUrl && msg.fileType?.startsWith("image/");
              const isOtherFile = msg.fileUrl && !isImage;

              const nextMsg = messages[idx + 1];
              const isLastInSequence = isAdmin && (!nextMsg || nextMsg.sender !== "Admin");

              const prevMsg = messages[idx - 1];
              const isSameAsPrev = prevMsg && prevMsg.sender === msg.sender;

              const msgDateObj = new Date(msg.createdAt);
              const msgLocalDate = msgDateObj.toDateString();
              let showDateDivider = false;

              if (msgLocalDate !== lastGroupDate) {
                showDateDivider = true;
                lastGroupDate = msgLocalDate;
              }

              return (
                <div key={msg.id} className={showDateDivider || !isSameAsPrev ? "mt-4" : "mt-1"}>
                  {showDateDivider && (
                    <div className="flex justify-center my-6">
                      <span className="bg-slate-200/80 text-slate-700 text-[11px] font-semibold tracking-wider uppercase px-3 py-1 rounded-full shadow-xs">
                        {getFormattedDateDivider(msg.createdAt)}
                      </span>
                    </div>
                  )}
                  <div className={`flex items-end gap-2 group relative ${isAdmin ? "justify-start" : "justify-end"}`}>
                    {isAdmin && (
                      isLastInSequence ? (
                        <img
                          src="/emily-avatar.png"
                          alt="Emily Walsh"
                          className="size-8 rounded-full object-cover shrink-0 mb-0.5"
                        />
                      ) : (
                        <div className="w-8 shrink-0" />
                      )
                    )}
                    
                    {/* Hover actions for Customer's own messages */}
                    {!isAdmin && editingMessageId !== msg.id && (
                      <div className="hidden group-hover:flex items-center gap-1.5 mr-1 shrink-0 bg-slate-50/90 border border-slate-200/50 shadow-xs px-1.5 py-0.5 rounded-lg transition-opacity duration-200">
                        <button
                          onClick={() => {
                            setEditingMessageId(msg.id);
                            setEditingText(msg.content);
                          }}
                          className="p-1 rounded-md hover:bg-slate-100 text-slate-500 hover:text-slate-700 transition-colors"
                          title="Edit"
                        >
                          <Pencil className="size-3.5" />
                        </button>
                        <button
                          onClick={() => handleDeleteMessage(msg.id)}
                          className="p-1 rounded-md hover:bg-slate-100 text-destructive/80 hover:text-destructive transition-colors"
                          title="Delete"
                        >
                          <Trash2 className="size-3.5" />
                        </button>
                      </div>
                    )}

                    <div
                      className={`max-w-[75%] rounded-2xl px-4 py-2.5 text-sm shadow-xs ${
                        isAdmin
                          ? tenant.brandName === "MindfulMe"
                            ? "bg-indigo-50/90 text-slate-800 rounded-tl-none border border-indigo-100/80"
                            : "bg-[#FFF5F2] text-slate-800 rounded-tl-none border border-[#FFE5DE]"
                          : tenant.brandName === "MindfulMe"
                            ? "bg-indigo-600 text-white rounded-tr-none"
                            : "bg-[#FF6F61] text-white rounded-tr-none"
                      }`}
                    >
                      {editingMessageId === msg.id ? (
                        <div className="space-y-2 min-w-[200px] text-slate-900">
                          <textarea
                            value={editingText}
                            onChange={(e) => setEditingText(e.target.value)}
                            className="w-full bg-white text-slate-900 border border-slate-300 min-h-[60px] text-xs p-2 rounded-lg focus:outline-none focus:ring-1 focus:ring-primary"
                          />
                          <div className="flex justify-end gap-1.5">
                            <button
                              className="h-6 text-[10px] px-2.5 text-slate-500 hover:bg-slate-100 border border-slate-200 rounded-md transition-colors"
                              onClick={() => {
                                setEditingMessageId(null);
                                setEditingText("");
                              }}
                            >
                              Cancel
                            </button>
                            <button
                              className="h-6 text-[10px] px-2.5 bg-primary text-white hover:bg-primary/95 rounded-md transition-colors"
                              onClick={() => handleUpdateMessage(msg.id)}
                            >
                              Save
                            </button>
                          </div>
                        </div>
                      ) : (
                        <>
                          {isImage ? (
                            <div className="space-y-1.5">
                              <img
                                src={msg.fileUrl}
                                alt={msg.fileName}
                                className="max-w-full max-h-56 rounded-lg object-cover cursor-pointer hover:opacity-90 transition-opacity"
                                onClick={() => window.open(msg.fileUrl, "_blank")}
                              />
                              {msg.content && msg.content !== `Sent an attachment: ${msg.fileName}` && (
                                <p className="leading-relaxed whitespace-pre-wrap break-words">{renderMessageTextWithLinks(msg.content)}</p>
                              )}
                            </div>
                          ) : msg.fileUrl && msg.fileType?.startsWith("video/") ? (
                            <div className="space-y-1.5">
                              <video
                                src={msg.fileUrl}
                                controls
                                className="max-w-full max-h-56 rounded-lg object-cover"
                              />
                              {msg.content && msg.content !== `Sent an attachment: ${msg.fileName}` && (
                                <p className="leading-relaxed whitespace-pre-wrap break-words">{renderMessageTextWithLinks(msg.content)}</p>
                              )}
                            </div>
                          ) : isOtherFile ? (
                            <div className="space-y-1.5">
                              <a
                                href={msg.fileUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={`flex items-center gap-2 rounded-xl p-2.5 border text-xs font-semibold ${
                                  isAdmin
                                    ? "bg-white/80 border-slate-200/80 text-slate-800 hover:bg-white"
                                    : "bg-white/10 border-white/20 text-white hover:bg-white/20"
                                }`}
                              >
                                <FileText className="size-5 shrink-0" />
                                <span className="truncate max-w-[180px]">{msg.fileName}</span>
                              </a>
                              {msg.content && msg.content !== `Sent an attachment: ${msg.fileName}` && (
                                <p className="leading-relaxed whitespace-pre-wrap break-words">{renderMessageTextWithLinks(msg.content)}</p>
                              )}
                            </div>
                          ) : (
                            <p className="leading-relaxed whitespace-pre-wrap break-words">{renderMessageTextWithLinks(msg.content)}</p>
                          )}

                          <span
                            className={`block text-[10px] mt-1.5 text-right ${
                              isAdmin ? "text-slate-500" : "text-white/70"
                            }`}
                          >
                            {msg.isEdited && <span className="mr-1 opacity-70 italic">(edited)</span>}
                            {msgDateObj.toLocaleTimeString([], {
                              hour: "2-digit",
                              minute: "2-digit",
                            })}
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
            </>
          )}
          <div ref={messagesEndRef} className="h-px w-full shrink-0" />
        </div>

        {/* Upload Progress Bar */}
        {uploadProgress !== null && (
          <div className="bg-white px-4 py-2 border-t border-border/20 flex items-center justify-between gap-4">
            <span className="text-xs text-muted-foreground flex items-center gap-2">
              <Loader2 className="size-3.5 animate-spin" />
              Uploading attachment…
            </span>
            <div className="flex-1 max-w-xs h-1.5 bg-muted rounded-full overflow-hidden">
              <div
                className="h-full bg-primary transition-all duration-300"
                style={{ width: `${uploadProgress}%` }}
              />
            </div>
            <span className="text-xs font-bold text-primary">{uploadProgress}%</span>
          </div>
        )}

        {/* Input form */}
        <form
          onSubmit={handleSend}
          className="p-3 border-t border-border/40 bg-white flex gap-2 items-center"
        >
          <input
            type="file"
            ref={imageInputRef}
            onChange={handleImageChange}
            className="hidden"
            accept="image/*,video/*"
          />
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileChange}
            className="hidden"
            accept="application/*,text/*"
          />
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={() => imageInputRef.current?.click()}
            className="rounded-xl h-11 w-11 shrink-0 text-muted-foreground hover:text-foreground"
            disabled={sending}
            title="Upload photo/video"
          >
            <Image className="size-5" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={() => fileInputRef.current?.click()}
            className="rounded-xl h-11 w-11 shrink-0 text-muted-foreground hover:text-foreground"
            disabled={sending}
            title="Upload document"
          >
            <Paperclip className="size-5" />
          </Button>
          <Textarea
            ref={textInputRef}
            value={inputText}
            onChange={(e) => setInputText(e.target.value)}
            onKeyDown={handleKeyDown}
            onFocus={() => {
              scrollToBottom(false);
              setTimeout(() => scrollToBottom(false), 50);
              setTimeout(() => scrollToBottom(false), 150);
            }}
            placeholder={t("chat_placeholder")}
            rows={1}
            className="flex-1 rounded-xl min-h-[44px] max-h-40 py-2.5 resize-none overflow-y-auto break-words [word-break:break-word]"
          />
          <Button
            type="submit"
            size="icon"
            className="rounded-xl h-11 w-11 shrink-0"
            disabled={!inputText.trim() || sending}
          >
            {sending && uploadProgress === null ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <Send className="size-4" />
            )}
          </Button>
        </form>
      </Card>
    </div>
  );
}
